import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useChatOps, UseChatOpsProps } from "../hooks/useChatOps";
import { ChatOpsFormSkeleton } from "../components/ChatOpsFormSkeleton";
import { useEffect } from "react";
import { ChatOpsFormBase } from "../components/ChatOpsFormBase";
import { NotificationType } from "~/operations";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";

interface SlackFormValues {
  activeState: boolean;
  url: string;
  conversationMode: boolean;
}

export function ChatOpsSlackForm(props: UseChatOpsProps) {
  const { isFeatureEnabled } = useFeatureFlags();
  const {
    integration,
    saveIntegration,
    hasChatOpsPermission,
    handleUrlChange,
  } = useChatOps(props);
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
    setValue,
    watch,
  } = useForm<SlackFormValues>({
    defaultValues: {
      activeState: false,
      url: "",
      conversationMode: false,
    },
    mode: "onChange",
  });

  const [activeState, url] = watch(["activeState", "url"]);

  useEffect(() => {
    if (integration) {
      reset({
        activeState: integration.enabled ?? false,
        url: integration.options?.url ?? "",
        conversationMode:
          integration.options?.notificationType ===
          NotificationType.AssetUpdates
            ? false
            : true,
      });
    }
  }, [integration]);

  // if there is no url and then the user updates the url, set the activeState to true
  useEffect(() => {
    handleUrlChange(url, activeState, setValue);
  }, [url]);

  const onSubmit = (data: SlackFormValues) => {
    if (!integration) return;

    const variables = {
      input: {
        mrn: integration.mrn,
        name: integration.name,
        enabled: data.activeState,
        options: {
          ...integration.options,
          url: data.url,
          notificationType: data.conversationMode
            ? NotificationType.TriageConversations
            : NotificationType.AssetUpdates,
        },
      },
    };
    saveIntegration(variables);
  };

  if (!integration) {
    return <ChatOpsFormSkeleton height={275} />;
  }

  return (
    <ChatOpsFormBase
      baseSubmit={handleSubmit(onSubmit)}
      isDirty={isDirty}
      isValid={isValid}
      hasPermission={hasChatOpsPermission}
    >
      <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Controller
          control={control}
          name="activeState"
          render={({ field }) => (
            <FormControlLabel
              labelPlacement="start"
              control={
                <Switch
                  color="primary"
                  checked={field.value}
                  onChange={field.onChange}
                />
              }
              label={field.value ? "Enabled" : "Disabled"}
            />
          )}
        />
      </Grid>
      {isFeatureEnabled("Slack Start Convo") && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Controller
            control={control}
            name="conversationMode"
            render={({ field }) => (
              <FormControlLabel
                labelPlacement="start"
                control={
                  <Switch
                    color="primary"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                }
                label={field.value ? "Triage Conversations" : "Asset Updates"}
              />
            )}
          />
        </Grid>
      )}
      <Grid size={12}>
        <Controller
          control={control}
          name="url"
          rules={{
            required: activeState ? "URL is required" : false,
          }}
          render={({ field }) => (
            <FormControl fullWidth>
              <FormLabel>
                <Typography variant="h6" color="textPrimary">
                  URL
                </Typography>
              </FormLabel>
              <TextField
                {...field}
                fullWidth
                error={!!errors.url && activeState}
                helperText={activeState ? errors.url?.message : ""}
              />
            </FormControl>
          )}
        />
      </Grid>
    </ChatOpsFormBase>
  );
}
